import * as React from 'react';
import { DayPicker } from 'react-day-picker';
import { ChevronLeft, ChevronRight } from 'lucide-react';

import { cn } from '@/lib/utils';
import { buttonVariants } from '@/components/ui/button';

export type CalendarProps = React.ComponentProps<typeof DayPicker>;

function Calendar({
  className,
  classNames,
  showOutsideDays = true,
  dir,
  locale,
  ...props
}: CalendarProps) {
  const isRTL = dir === 'rtl';

  // French day initials array (Dimanche, Lundi, Mardi, Mercredi, Jeudi, Vendredi, Samedi)
  // Index corresponds to weekday: 0 = Sunday, 1 = Monday, etc.
  const frenchDayInitials = ['D', 'L', 'M', 'M', 'J', 'V', 'S'];

  // Custom weekday component to show only French day initials (always use French, even in Arabic mode)
  const Weekday = ({ weekday }: { weekday: number }) => {
    // weekday is a number from 0 (Sunday) to 6 (Saturday)
    return <span>{frenchDayInitials[weekday]}</span>;
  };

  return (
    <DayPicker
      showOutsideDays={showOutsideDays}
      {...(dir ? { dir } : {})}
      {...(locale ? { locale } : {})}
      className={cn('p-3', className)}
      classNames={{
        months: 'relative flex flex-col sm:flex-row gap-4',
        month: 'w-full',
        month_caption: 'relative mx-10 mb-1 flex h-8 items-center justify-center z-20',
        caption_label: 'text-sm font-medium',
        nav: 'absolute top-0 flex w-full justify-between z-10',
        button_previous: cn(
          buttonVariants({ variant: 'ghost' }),
          'size-8 text-muted-foreground/80 hover:text-foreground p-0'
        ),
        button_next: cn(
          buttonVariants({ variant: 'ghost' }),
          'size-8 text-muted-foreground/80 hover:text-foreground p-0'
        ),
        weekday: 'size-8 p-0 text-xs font-medium text-muted-foreground/80',
        day_button: cn(
          'cursor-pointer relative flex size-8 items-center justify-center whitespace-nowrap rounded-md p-0 text-foreground transition-all',
          'group-data-disabled:pointer-events-none focus-visible:z-10',
          'hover:bg-accent hover:text-foreground',
          'group-data-selected:bg-yellow-400 group-data-selected:text-foreground group-data-selected:font-semibold',
          'group-data-disabled:text-foreground/30 group-data-disabled:line-through',
          'group-data-outside:text-foreground/30',
          'outline-none focus-visible:ring-ring/50 focus-visible:ring-2'
        ),
        day: 'group size-8 px-0 py-px text-sm relative',
        weeks: 'w-full border-collapse',
        week: 'flex w-full mt-2',
        today: cn(
          "relative",
          "after:absolute after:inset-x-1/2 after:bottom-1.5 after:z-10 after:h-0.5 after:w-4 after:-translate-x-1/2 after:rounded-[2px]",
          "after:bg-yellow-400"
        ),
        outside: 'text-muted-foreground/50 aria-selected:bg-accent/50',
        selected: 'bg-yellow-400 text-foreground font-semibold hover:bg-yellow-400 hover:text-foreground focus:bg-yellow-400 focus:text-foreground',
        range_start: 'bg-yellow-400 text-foreground rounded-s-md rounded-e-none',
        range_end: 'bg-yellow-400 text-foreground rounded-e-md rounded-s-none',
        range_middle: 'bg-yellow-400/20 text-foreground rounded-none',
        ...classNames,
      }}
      components={{
        Chevron: ({ orientation, ...chevronProps }) =>
          orientation === 'left' ? (
            <ChevronLeft className={cn('h-4 w-4', isRTL && 'rotate-180')} {...chevronProps} />
          ) : (
            <ChevronRight className={cn('h-4 w-4', isRTL && 'rotate-180')} {...chevronProps} />
          ),
        Weekday: (weekdayProps: any) => <Weekday weekday={weekdayProps.weekday} />,
      }}
      {...props}
    />
  );
}
Calendar.displayName = 'Calendar';

export { Calendar };
